import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMediaQuery, useTheme } from '@mui/material';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Box,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  Paper,
  Divider,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  LinearProgress,
  Backdrop,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Lightbulb as LightbulbIcon,
  ArrowBack as ArrowBackIcon,
  AutoAwesome as AutoAwesomeIcon,
  Computer as ComputerIcon,
} from '@mui/icons-material';
import Sidebar, { drawerWidth, collapsedDrawerWidth } from '../components/Sidebar';
import { useAuth } from '../contexts/AuthContext';

const MODULE_NAMES = [
  'Sichere Passwörter',
  'MFA',
  'Passwort-Manager',
  'Backups',
  'Automatische Updates',
  'Email Account absichern',
  'Ad-Blocker',
  'Router Passwort ändern',
  'Social Media',
  'App-Berechtigungen',
];

const OPERATING_SYSTEMS = [
  'Windows 11',
  'Windows 10',
  'macOS',
  'Linux',
  'Android',
  'iOS',
  'Anderes',
];

// Konfiguration für modulspezifische Parameter
const MODULE_PARAMETERS = {
  'Sichere Passwörter': {
    parameters: [
      {
        id: 'device_type',
        label: 'Gerätetyp',
        options: ['Desktop/Laptop', 'Smartphone', 'Tablet', 'Alle Geräte'],
        required: true,
      },
      {
        id: 'use_case',
        label: 'Verwendungszweck',
        options: ['Persönliche Konten', 'Geschäftliche Konten', 'Beides'],
        required: true,
      },
    ],
  },
  'MFA': {
    parameters: [
      {
        id: 'account_type',
        label: 'Kontotyp',
        options: ['E-Mail', 'Banking', 'Social Media', 'Cloud-Dienste', 'Alle wichtigen Konten'],
        required: true,
      },
      {
        id: 'auth_method',
        label: 'Authentifizierungsmethode',
        options: ['Authenticator-App (empfohlen)', 'SMS', 'E-Mail', 'Hardware-Token'],
        required: true,
      },
      {
        id: 'device_type',
        label: 'Gerätetyp',
        options: ['Smartphone', 'Tablet', 'Desktop/Laptop'],
        required: true,
      },
    ],
  },
  'Passwort-Manager': {
    parameters: [
      {
        id: 'manager_choice',
        label: 'Passwort-Manager',
        options: ['Bitwarden (empfohlen)', '1Password', 'KeePass', 'LastPass', 'Noch nicht entschieden'],
        required: true,
      },
      {
        id: 'device_type',
        label: 'Hauptgerät',
        options: ['Desktop/Laptop', 'Smartphone', 'Tablet'],
        required: true,
      },
      {
        id: 'sync_preference',
        label: 'Synchronisierung',
        options: ['Cloud-Sync (empfohlen)', 'Lokal speichern', 'Beides'],
        required: true,
      },
    ],
  },
  'Backups': {
    parameters: [
      {
        id: 'backup_type',
        label: 'Backup-Typ',
        options: ['Externe Festplatte', 'Cloud-Speicher', 'Beides'],
        required: true,
      },
      {
        id: 'data_type',
        label: 'Zu sichernde Daten',
        options: ['Dokumente', 'Fotos', 'Videos', 'Alle Daten'],
        required: true,
      },
      {
        id: 'device_type',
        label: 'Gerätetyp',
        options: ['Desktop/Laptop', 'Smartphone', 'Tablet', 'Alle Geräte'],
        required: true,
      },
    ],
  },
  'Automatische Updates': {
    parameters: [
      {
        id: 'update_scope',
        label: 'Update-Bereich',
        options: ['Nur Betriebssystem', 'Betriebssystem + Apps', 'Alle Programme'],
        required: true,
      },
      {
        id: 'update_time',
        label: 'Update-Zeitpunkt',
        options: ['Sofort installieren', 'Bei nächstem Neustart', 'Nur während Ruhezeiten'],
        required: true,
      },
    ],
  },
  'Email Account absichern': {
    parameters: [
      {
        id: 'email_provider',
        label: 'E-Mail-Provider',
        options: ['Gmail', 'Outlook/Hotmail', 'Apple iCloud', 'Anderer Provider'],
        required: true,
      },
      {
        id: 'security_level',
        label: 'Sicherheitsstufe',
        options: ['Basis (2FA)', 'Erweitert (App-Passwörter)', 'Maximal (Hardware-Token)'],
        required: true,
      },
    ],
  },
  'Ad-Blocker': {
    parameters: [
      {
        id: 'browser',
        label: 'Browser',
        options: ['Chrome', 'Firefox', 'Edge', 'Safari', 'Brave', 'Anderer'],
        required: true,
      },
      {
        id: 'blocking_level',
        label: 'Blockierungsstufe',
        options: ['Standard (empfohlen)', 'Strikt', 'Anpassbar'],
        required: true,
      },
    ],
  },
  'Router Passwort ändern': {
    parameters: [
      {
        id: 'router_brand',
        label: 'Router-Marke',
        options: ['FritzBox', 'TP-Link', 'Netgear', 'Linksys', 'ASUS', 'Andere'],
        required: true,
      },
      {
        id: 'encryption_type',
        label: 'Verschlüsselung',
        options: ['WPA3 (empfohlen)', 'WPA2', 'Beides unterstützen'],
        required: true,
      },
    ],
  },
  'Social Media': {
    parameters: [
      {
        id: 'platform',
        label: 'Plattform',
        options: ['Facebook', 'Instagram', 'Twitter/X', 'LinkedIn', 'TikTok', 'Alle Plattformen'],
        required: true,
      },
      {
        id: 'privacy_level',
        label: 'Privatsphäre-Stufe',
        options: ['Basis', 'Erweitert', 'Maximal'],
        required: true,
      },
    ],
  },
  'App-Berechtigungen': {
    parameters: [
      {
        id: 'device_type',
        label: 'Gerätetyp',
        options: ['Android', 'iOS'],
        required: true,
      },
      {
        id: 'review_scope',
        label: 'Überprüfungsbereich',
        options: ['Alle Apps', 'Nur neue Apps', 'Spezifische Apps'],
        required: true,
      },
    ],
  },
};

const ModuleGuideGenerator = () => {
  const navigate = useNavigate();
  const { moduleName: moduleNameParam } = useParams();
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  const [moduleName, setModuleName] = useState('');
  const [operatingSystem, setOperatingSystem] = useState('');
  const [moduleParameters, setModuleParameters] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [error, setError] = useState('');
  const [guide, setGuide] = useState(null);
  const [rateLimitStatus, setRateLimitStatus] = useState({
    remaining: 10,
    used: 0,
    limit: 10,
    reset_in_minutes: 0
  });

  // Set module name from URL parameter if present
  useEffect(() => {
    if (moduleNameParam) {
      const decodedModuleName = decodeURIComponent(moduleNameParam);
      // Verify it's a valid module name
      if (MODULE_NAMES.includes(decodedModuleName)) {
        setModuleName(decodedModuleName);
        // Reset parameters when module changes
        setModuleParameters({});
      }
    }
  }, [moduleNameParam]);

  // Reset parameters when module changes
  useEffect(() => {
    if (moduleName) {
      setModuleParameters({});
    }
  }, [moduleName]);

  // Fetch rate limit status on mount and periodically
  useEffect(() => {
    const fetchRateLimitStatus = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;

        const response = await fetch('/api/v1/module-guide/rate-limit-status', {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          setRateLimitStatus(data);
        }
      } catch (error) {
        console.error('Error fetching rate limit status:', error);
      }
    };

    fetchRateLimitStatus();
    // Update every 30 seconds
    const interval = setInterval(fetchRateLimitStatus, 30000);
    return () => clearInterval(interval);
  }, []);

  if (!user) {
    return null;
  }

  // Redirect if no module is specified
  if (!moduleNameParam && !moduleName) {
    return (
      <Box sx={{ 
        background: 'linear-gradient(135deg, #0e0e0e 0%, #1a1a1a 50%, #0e0e0e 100%)',
        minHeight: '100vh', 
        color: '#ffffff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <Container maxWidth="sm">
          {/* Zurück zum Lagebild Link */}
          <Box sx={{ mb: 2 }}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/dashboard')}
              sx={{
                color: '#42a5f5',
                '&:hover': {
                  backgroundColor: 'rgba(66, 165, 245, 0.1)',
                },
              }}
            >
              Zurück zum Lagebild
            </Button>
          </Box>
          
          <Card sx={{ 
            background: 'linear-gradient(135deg, rgba(26, 26, 26, 0.9) 0%, rgba(15, 15, 15, 0.9) 100%)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
            borderRadius: 3,
          }}>
            <CardContent sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="h5" sx={{ mb: 2, color: '#ffffff' }}>
                Kein Modul ausgewählt
              </Typography>
              <Typography variant="body1" sx={{ mb: 3, color: '#b0b0b0' }}>
                Bitte wähle ein Modul aus der Checkliste aus, um eine Anleitung zu erstellen.
              </Typography>
              <Button
                variant="contained"
                onClick={() => navigate('/checklist')}
                sx={{
                  backgroundColor: '#42a5f5',
                  color: '#ffffff',
                  '&:hover': {
                    backgroundColor: '#2196f3',
                  },
                }}
              >
                Zur Checkliste
              </Button>
            </CardContent>
          </Card>
        </Container>
      </Box>
    );
  }

  const handleGenerate = async () => {
    if (!moduleName) {
      setError('Bitte wählen Sie ein Modul aus.');
      return;
    }

    // Check rate limit before proceeding
    if (rateLimitStatus.remaining <= 0) {
      setError(`Rate Limit erreicht. Sie haben bereits ${rateLimitStatus.used} von ${rateLimitStatus.limit} Anfragen in der letzten Stunde verwendet. Bitte versuchen Sie es in ${rateLimitStatus.reset_in_minutes} Minuten erneut.`);
      return;
    }

    // Validate required parameters
    const moduleConfig = MODULE_PARAMETERS[moduleName];
    if (moduleConfig) {
      const missingParams = moduleConfig.parameters
        .filter(param => param.required && !moduleParameters[param.id])
        .map(param => param.label);
      
      if (missingParams.length > 0) {
        setError(`Bitte füllen Sie alle Pflichtfelder aus: ${missingParams.join(', ')}`);
        return;
      }
    }

    setLoading(true);
    setLoadingProgress(0);
    setLoadingMessage('Bereite Anfrage vor...');
    setError('');
    setGuide(null);

    let progressInterval;

    try {
      // Simulate progress for better UX (stops at 85% to leave room for actual completion)
      progressInterval = setInterval(() => {
        setLoadingProgress(prev => {
          if (prev >= 85) return prev; // Stop at 85% instead of 90% to prevent reaching 97%
          return prev + Math.random() * 5; // Smaller increments
        });
      }, 500);

      // Build user details from parameters
      setLoadingMessage('Verarbeite Ihre Angaben...');
      setLoadingProgress(10);
      const userDetailsParts = [];
      if (operatingSystem) {
        userDetailsParts.push(`Betriebssystem: ${operatingSystem}`);
      }
      
      if (moduleConfig) {
        moduleConfig.parameters.forEach(param => {
          if (moduleParameters[param.id]) {
            userDetailsParts.push(`${param.label}: ${moduleParameters[param.id]}`);
          }
        });
      }
      
      const userDetails = userDetailsParts.join('\n');

      // Get CSRF token
      setLoadingMessage('Verbinde mit Server...');
      setLoadingProgress(20);
      const token = localStorage.getItem('token');
      const csrfResponse = await fetch('/api/v1/csrf-token', {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      const csrfData = await csrfResponse.json();
      const csrfToken = csrfData.csrf_token;

      setLoadingMessage('Generiere personalisierte Anleitung mit ChatGPT...');
      setLoadingProgress(40);

      // Create AbortController for timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 150000); // 150 seconds timeout (backend has 120s)

      const response = await fetch('/api/v1/module-guide/generate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          module_name: moduleName,
          user_details: userDetails,
          operating_system: operatingSystem || null,
          locale: 'de',
        }),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);
      setLoadingProgress(80);
      setLoadingMessage('Verarbeite Antwort...');

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Fehler beim Generieren der Anleitung');
      }

      const data = await response.json();
      setLoadingProgress(100);
      setLoadingMessage('Anleitung erfolgreich generiert!');
      
      // Small delay to show completion
      setTimeout(() => {
        setGuide(data);
        if (progressInterval) clearInterval(progressInterval);
        // Update rate limit status after successful generation
        const fetchRateLimitStatus = async () => {
          try {
            const token = localStorage.getItem('token');
            if (token) {
              const statusResponse = await fetch('/api/v1/module-guide/rate-limit-status', {
                headers: { 'Authorization': `Bearer ${token}` },
              });
              if (statusResponse.ok) {
                const statusData = await statusResponse.json();
                setRateLimitStatus(statusData);
              }
            }
          } catch (error) {
            console.error('Error fetching rate limit status:', error);
          }
        };
        fetchRateLimitStatus();
      }, 300);
    } catch (err) {
      console.error('Error generating guide:', err);
      
      // Clear progress interval immediately
      if (progressInterval) {
        clearInterval(progressInterval);
        progressInterval = null;
      }
      
      // Handle timeout/abort errors
      if (err.name === 'AbortError' || err.message?.includes('timeout') || err.message?.includes('aborted')) {
        setError('Die Anfrage hat zu lange gedauert. Bitte versuchen Sie es erneut. Die Generierung kann bis zu 2 Minuten dauern.');
      } else {
        setError(err.message || 'Fehler beim Generieren der Anleitung. Bitte versuchen Sie es erneut.');
      }
      
      setLoadingProgress(0);
      setLoadingMessage('');
    } finally {
      // Ensure progress interval is cleared
      if (progressInterval) {
        clearInterval(progressInterval);
      }
      
      setTimeout(() => {
        setLoading(false);
        setLoadingProgress(0);
        setLoadingMessage('');
      }, 500);
    }
  };

  const handleReset = () => {
    setModuleName('');
    setOperatingSystem('');
    setModuleParameters({});
    setGuide(null);
    setError('');
  };

  const handleParameterChange = (paramId, value) => {
    setModuleParameters(prev => ({
      ...prev,
      [paramId]: value,
    }));
  };

  const getModuleConfig = () => {
    return MODULE_PARAMETERS[moduleName] || null;
  };

  const contentWidth = sidebarOpen ? `calc(100% - ${drawerWidth}px)` : `calc(100% - ${collapsedDrawerWidth}px)`;

  return (
    <Box sx={{ 
      background: 'linear-gradient(135deg, #0e0e0e 0%, #1a1a1a 50%, #0e0e0e 100%)',
      minHeight: '100vh', 
      color: '#ffffff',
      position: 'relative',
      width: '100%',
      maxWidth: '100vw',
      overflowX: 'hidden',
      '&::before': {
        content: '""',
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'radial-gradient(circle at 20% 80%, rgba(33, 150, 243, 0.1) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(66, 165, 245, 0.1) 0%, transparent 50%)',
        pointerEvents: 'none',
        zIndex: 0,
      }
    }}>
      <Sidebar 
        open={sidebarOpen} 
        onToggle={() => setSidebarOpen(!sidebarOpen)}
        mobileOpen={mobileSidebarOpen}
        onMobileToggle={() => setMobileSidebarOpen(!mobileSidebarOpen)}
      />
      
      <Box
        sx={{
          display: 'flex',
          marginLeft: { xs: 0, sm: sidebarOpen ? `${drawerWidth}px` : `${collapsedDrawerWidth}px` },
          transition: theme.transitions.create(['margin-left'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
          width: { xs: '100%', sm: contentWidth },
          padding: { xs: 2, sm: 3, md: 4 },
          paddingBottom: 4,
        }}
      >
        <Container maxWidth="lg">
          {/* Zurück zum Lagebild Link */}
          <Box sx={{ mb: 2 }}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/dashboard')}
              sx={{
                color: '#42a5f5',
                '&:hover': {
                  backgroundColor: 'rgba(66, 165, 245, 0.1)',
                },
              }}
            >
              Zurück zum Lagebild
            </Button>
          </Box>
          
          {/* Header */}
          <Box sx={{ mb: 4 }}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/checklist')}
              sx={{ 
                mb: 2,
                color: '#ffffff',
                borderColor: 'rgba(255, 255, 255, 0.3)',
                '&:hover': {
                  borderColor: 'rgba(255, 255, 255, 0.5)',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                }
              }}
              variant="outlined"
            >
              Zurück zur Checkliste
            </Button>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <AutoAwesomeIcon sx={{ fontSize: 40, color: '#42a5f5' }} />
              <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#ffffff' }}>
                Anleitungsgenerator
              </Typography>
            </Box>
            <Typography variant="body1" sx={{ color: '#b0b0b0', maxWidth: '800px' }}>
              Erhalte eine personalisierte, schrittweise Anleitung zur Umsetzung eines Sicherheitsmoduls. 
              Wähle die relevanten Parameter aus, und die AI erstellt eine maßgeschneiderte, detaillierte Anleitung für dich.
            </Typography>
          </Box>

          {!guide ? (
            /* Input Form */
            <Card sx={{ 
              mb: 4,
              background: 'linear-gradient(135deg, rgba(26, 26, 26, 0.9) 0%, rgba(15, 15, 15, 0.9) 100%)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
              borderRadius: 3,
            }}>
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#ffffff' }}>
                    Anleitung generieren
                  </Typography>
                  <Chip
                    label={`${rateLimitStatus.remaining} von ${rateLimitStatus.limit} Anfragen übrig`}
                    color={rateLimitStatus.remaining > 3 ? 'success' : rateLimitStatus.remaining > 0 ? 'warning' : 'error'}
                    sx={{
                      fontWeight: 600,
                    }}
                  />
                </Box>

                {rateLimitStatus.remaining === 0 && (
                  <Alert severity="warning" sx={{ mb: 3 }}>
                    Rate Limit erreicht. Sie haben bereits {rateLimitStatus.used} von {rateLimitStatus.limit} Anfragen in der letzten Stunde verwendet.
                    {rateLimitStatus.reset_in_minutes > 0 && (
                      <> Bitte versuchen Sie es in {rateLimitStatus.reset_in_minutes} Minuten erneut.</>
                    )}
                  </Alert>
                )}

                {rateLimitStatus.remaining > 0 && rateLimitStatus.remaining <= 3 && (
                  <Alert severity="info" sx={{ mb: 3 }}>
                    Sie haben noch {rateLimitStatus.remaining} von {rateLimitStatus.limit} Anfragen in dieser Stunde.
                  </Alert>
                )}

                {error && (
                  <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                  </Alert>
                )}

                {/* Loading Progress Bar */}
                {loading && (
                  <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" sx={{ color: '#42a5f5', fontWeight: 500 }}>
                        {loadingMessage || 'Generiere Anleitung...'}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#b0b0b0' }}>
                        {Math.round(loadingProgress)}%
                      </Typography>
                    </Box>
                    <LinearProgress 
                      variant="determinate" 
                      value={loadingProgress} 
                      sx={{
                        height: 8,
                        borderRadius: 4,
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        '& .MuiLinearProgress-bar': {
                          borderRadius: 4,
                          backgroundColor: '#42a5f5',
                        },
                      }}
                    />
                    <Typography variant="caption" sx={{ mt: 1, display: 'block', color: '#b0b0b0', fontStyle: 'italic' }}>
                      Dies kann einige Sekunden dauern...
                    </Typography>
                  </Box>
                )}

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  {/* Module Name Display */}
                  {moduleName && (
                    <Box sx={{ 
                      p: 2, 
                      backgroundColor: 'rgba(66, 165, 245, 0.1)',
                      border: '1px solid rgba(66, 165, 245, 0.3)',
                      borderRadius: 2,
                      mb: 1,
                    }}>
                      <Typography variant="body1" sx={{ color: '#42a5f5', fontWeight: 600 }}>
                        Modul: {moduleName}
                      </Typography>
                    </Box>
                  )}

                  {/* Operating System */}
                  <FormControl fullWidth>
                    <InputLabel id="os-select-label" sx={{ color: '#ffffff' }}>
                      Betriebssystem {getModuleConfig()?.parameters.find(p => p.id === 'device_type') ? '(optional)' : ''}
                    </InputLabel>
                    <Select
                      labelId="os-select-label"
                      value={operatingSystem}
                      onChange={(e) => setOperatingSystem(e.target.value)}
                      label="Betriebssystem"
                      sx={{
                        color: '#ffffff',
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(255, 255, 255, 0.2)',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(255, 255, 255, 0.3)',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#42a5f5',
                        },
                      }}
                    >
                      <MenuItem value="">
                        <em>Keine Angabe</em>
                      </MenuItem>
                      {OPERATING_SYSTEMS.map((os) => (
                        <MenuItem key={os} value={os}>
                          {os}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {/* Module-specific Parameters */}
                  {getModuleConfig() && getModuleConfig().parameters.map((param) => (
                    <FormControl key={param.id} fullWidth>
                      <InputLabel id={`${param.id}-label`} sx={{ color: '#ffffff' }}>
                        {param.label} {param.required ? '*' : ''}
                      </InputLabel>
                      <Select
                        labelId={`${param.id}-label`}
                        value={moduleParameters[param.id] || ''}
                        onChange={(e) => handleParameterChange(param.id, e.target.value)}
                        label={param.label}
                        required={param.required}
                        sx={{
                          color: '#ffffff',
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(255, 255, 255, 0.2)',
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(255, 255, 255, 0.3)',
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#42a5f5',
                          },
                        }}
                      >
                        <MenuItem value="">
                          <em>Bitte auswählen</em>
                        </MenuItem>
                        {param.options.map((option) => (
                          <MenuItem key={option} value={option}>
                            {option}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ))}

                  {!getModuleConfig() && moduleName && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      Für dieses Modul sind keine spezifischen Parameter erforderlich. Wähle optional ein Betriebssystem aus.
                    </Alert>
                  )}

                  {/* Generate Button */}
                  <Button
                    variant="contained"
                    size="large"
                    onClick={handleGenerate}
                    disabled={loading || !moduleName || rateLimitStatus.remaining <= 0}
                    startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <AutoAwesomeIcon />}
                    sx={{
                      backgroundColor: loading ? 'rgba(66, 165, 245, 0.6)' : '#42a5f5',
                      color: '#ffffff',
                      '&:hover': {
                        backgroundColor: loading ? 'rgba(66, 165, 245, 0.6)' : '#2196f3',
                      },
                      '&:disabled': {
                        backgroundColor: 'rgba(66, 165, 245, 0.3)',
                        color: 'rgba(255, 255, 255, 0.5)',
                      },
                      py: 1.5,
                      fontSize: '1.1rem',
                      borderRadius: 2,
                    }}
                  >
                    {loading ? 'Generiere Anleitung...' : 'Anleitung generieren'}
                  </Button>
                </Box>
              </CardContent>
            </Card>
          ) : (
            /* Generated Guide Display */
            <Box>
              <Card sx={{ 
                mb: 3,
                background: 'linear-gradient(135deg, rgba(26, 26, 26, 0.9) 0%, rgba(15, 15, 15, 0.9) 100%)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                borderRadius: 3,
              }}>
                <CardContent sx={{ p: 4 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h4" sx={{ fontWeight: 600, color: '#ffffff' }}>
                      {guide.title}
                    </Typography>
                    <Button
                      variant="outlined"
                      onClick={handleReset}
                      sx={{
                        color: '#ffffff',
                        borderColor: 'rgba(255, 255, 255, 0.3)',
                        '&:hover': {
                          borderColor: '#42a5f5',
                          backgroundColor: 'rgba(66, 165, 245, 0.1)',
                        },
                      }}
                    >
                      Neue Anleitung
                    </Button>
                  </Box>

                  {/* Introduction */}
                  <Paper sx={{ 
                    p: 3, 
                    mb: 3, 
                    backgroundColor: 'rgba(66, 165, 245, 0.1)',
                    border: '1px solid rgba(66, 165, 245, 0.3)',
                    borderRadius: 2,
                  }}>
                    <Typography variant="body1" sx={{ lineHeight: 1.8, color: '#ffffff' }}>
                      {guide.introduction}
                    </Typography>
                  </Paper>

                  {/* Prerequisites */}
                  {guide.prerequisites && guide.prerequisites.length > 0 && (
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#ffffff' }}>
                        Voraussetzungen
                      </Typography>
                      <List>
                        {guide.prerequisites.map((prereq, index) => (
                          <ListItem key={index} sx={{ pl: 0 }}>
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: '#4caf50' }} />
                            </ListItemIcon>
                            <ListItemText primary={prereq} primaryTypographyProps={{ sx: { color: '#ffffff' } }} />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  )}

                  <Divider sx={{ my: 3, borderColor: 'rgba(255, 255, 255, 0.2)' }} />

                  {/* Steps */}
                  {guide.steps && guide.steps.length > 0 && (
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#ffffff' }}>
                        Schritt-für-Schritt Anleitung
                      </Typography>
                      {guide.steps.map((step, index) => (
                        <Accordion 
                          key={index}
                          defaultExpanded={index === 0}
                          sx={{
                            mb: 2,
                            borderRadius: 3,
                            background: 'linear-gradient(135deg, rgba(26, 26, 26, 0.9) 0%, rgba(15, 15, 15, 0.9) 100%)',
                            border: '1px solid rgba(255, 255, 255, 0.1)',
                            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                            '&:hover': {
                              boxShadow: '0 12px 40px rgba(33, 150, 243, 0.3)',
                              border: '1px solid rgba(66, 165, 245, 0.3)',
                            },
                            '&:before': {
                              display: 'none',
                            },
                            '&.Mui-expanded': {
                              borderRadius: 3,
                            },
                          }}
                        >
                          <AccordionSummary
                            expandIcon={<ExpandMoreIcon sx={{ color: '#ffffff' }} />}
                            sx={{
                              '& .MuiAccordionSummary-content': {
                                alignItems: 'center',
                                gap: 2,
                              },
                            }}
                          >
                            <Chip 
                              label={`Schritt ${step.number || index + 1}`}
                              sx={{ 
                                backgroundColor: '#42a5f5',
                                color: '#ffffff',
                                fontWeight: 600,
                              }}
                            />
                            <Typography variant="h6" sx={{ fontWeight: 600, color: '#ffffff' }}>
                              {step.title || `Schritt ${index + 1}`}
                            </Typography>
                          </AccordionSummary>
                          <AccordionDetails>
                            <Typography variant="body2" sx={{ mb: 2, color: '#b0b0b0', lineHeight: 1.6 }}>
                              {step.description}
                            </Typography>
                            {step.details && (
                              <Typography variant="body2" sx={{ color: '#d0d0d0', fontStyle: 'italic', lineHeight: 1.6 }}>
                                {step.details}
                              </Typography>
                            )}
                          </AccordionDetails>
                        </Accordion>
                      ))}
                    </Box>
                  )}

                  {/* Verification */}
                  {guide.verification && (
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#ffffff' }}>
                        Überprüfung
                      </Typography>
                      <Paper sx={{ 
                        p: 2, 
                        backgroundColor: 'rgba(76, 175, 80, 0.1)',
                        border: '1px solid rgba(76, 175, 80, 0.3)',
                        borderRadius: 2,
                      }}>
                        <Typography variant="body1" sx={{ color: '#ffffff', lineHeight: 1.6 }}>
                          {guide.verification}
                        </Typography>
                      </Paper>
                    </Box>
                  )}

                  {/* Tips */}
                  {guide.tips && guide.tips.length > 0 && (
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#ffffff', display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LightbulbIcon sx={{ color: '#ffc107' }} />
                        Tipps und Hinweise
                      </Typography>
                      <List>
                        {guide.tips.map((tip, index) => (
                          <ListItem key={index} sx={{ pl: 0 }}>
                            <ListItemIcon>
                              <LightbulbIcon sx={{ color: '#ffc107' }} />
                            </ListItemIcon>
                            <ListItemText primary={tip} primaryTypographyProps={{ sx: { color: '#ffffff' } }} />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  )}

                  {/* Warnings */}
                  {guide.warnings && guide.warnings.length > 0 && (
                    <Box>
                      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#ffffff', display: 'flex', alignItems: 'center', gap: 1 }}>
                        <WarningIcon sx={{ color: '#ff9800' }} />
                        Wichtige Warnungen
                      </Typography>
                      <List>
                        {guide.warnings.map((warning, index) => (
                          <ListItem key={index} sx={{ pl: 0 }}>
                            <ListItemIcon>
                              <WarningIcon sx={{ color: '#ff9800' }} />
                            </ListItemIcon>
                            <ListItemText 
                              primary={warning}
                              primaryTypographyProps={{ sx: { color: '#ff9800' } }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Box>
          )}
        </Container>
      </Box>
    </Box>
  );
};

export default ModuleGuideGenerator;

